import { useState, useCallback, useRef } from 'react';
import { apiService } from "@/api/api";
import { useToast } from "@/hooks/use-toast";

// Constants for the polling mechanism
const POLLING_INTERVAL_MS = 10000; // Poll every 10 seconds
const MAX_POLLS = 36; // Max 36 polls (e.g., 6-minute timeout)

// The new hook to manage the entire async workflow
export const useDictationProcessor = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState('');
  const { toast } = useToast();

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // This is the main function your component will call.
  const startProcessingSession = useCallback(async (
    dictationData: { template_type: string; dictation_notes: string; client_id?: string; }
  ): Promise<{ success: boolean; enhanced_sections?: any; session_id?: string; error?: string }> => {

    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingStep('Sending request to server...');
    let session_id = ''; // Variable to hold the session_id across the function scope

    stopPolling(); // Clear any previous polling intervals

    try {
      // --- STEP 1: Make the initial fast request to start the job ---
      const initialResponse = await apiService.processDictation(dictationData);
      
      if (!initialResponse?.success || !initialResponse.data?.session_id) {
        throw new Error("Failed to initiate processing session. No session ID was returned from the server.");
      }
      
      session_id = initialResponse.data.session_id;
      setProcessingProgress(10);
      setProcessingStep('Request accepted. AI is now processing...');
      console.log(`✅ Job successfully started with Session ID: ${session_id}`);

      // --- STEP 2: Start polling for the result using the session ID ---
      return new Promise((resolve, reject) => {
        let pollCount = 0;
        
        pollingIntervalRef.current = setInterval(async () => {
          pollCount++;
          console.log(`Polling attempt #${pollCount} for session ${session_id}`);
          
          if (pollCount > MAX_POLLS) {
            stopPolling();
            toast({ title: "Processing Timeout", description: "The request took too long to process.", variant: "destructive" });
            setIsProcessing(false);
            return reject(new Error("Processing timed out."));
          }

          try {
            const statusResponse = await apiService.getSessionById(session_id);

            if (statusResponse?.success && statusResponse.data) {
              const session = statusResponse.data;
              console.log(`Session status for ${session_id}:`, session);
              
              // ADD DEBUG LOGGING TO SEE ACTUAL STATUS
              console.log('🔍 Session status value:', session.status);
              console.log('🔍 Session has enhanced_sections:', !!session.enhanced_sections);
              
              // UPDATED LOGIC: Check for multiple possible completion states
              const isComplete = session.status === 'ready_for_review' || 
                                session.status === 'completed' || 
                                session.status === 'done' ||
                                (session.enhanced_sections && session.processing_duration_seconds);
              
              const isError = session.status === 'error' || 
                             session.status === 'failed';
              
              if (isComplete) {
                stopPolling();
                setProcessingProgress(100);
                setProcessingStep('Processing complete!');
                setIsProcessing(false);
                
                console.log('✅ Processing completed successfully!');
                resolve({ 
                  success: true, 
                  enhanced_sections: session.enhanced_sections,
                  session_id: session_id
                });

              } else if (isError) {
                stopPolling();
                const errorMessage = session.error_message || "An unknown error occurred in the background worker.";
                toast({ title: "Processing Failed", description: errorMessage, variant: "destructive" });
                setIsProcessing(false);
                reject(new Error(errorMessage));

              } else {
                // Still processing
                console.log('⏳ Still processing, current status:', session.session_status);
                setProcessingStep('AI is processing notes...');
                setProcessingProgress(prev => Math.min(prev + 2.5, 95));
              }
            } else {
              console.warn('❌ Invalid response from getSessionById:', statusResponse);
            }
          } catch (pollError) {
            console.error(`Polling error for session ${session_id}:`, pollError);
          }
        }, POLLING_INTERVAL_MS);
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
      stopPolling();
      setIsProcessing(false);
      return { success: false, error: errorMessage };
    }
  }, [toast, stopPolling]);

  return {
    isProcessing,
    processingProgress,
    processingStep,
    startProcessingSession,
  };
};